import React from "react";
import { Helmet } from "react-helmet";
import { WithContext } from "schema-dts";
import moment from "moment";
import { TAX_RULES, calcTaxInclusivePriceSafe, buildTaxRulesFromSettings } from "_components/CourseStickyPanel/helper";
import { normalize } from "_components/CourseSession/CourseSession.utils";


function htmlListToStringArray(html?: string): string[] {
  if (!html) return [];

  const raw = html.trim();
  if (!raw) return [];

  // ✅ NEW: no HTML tags at all → treat as single string
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(raw);
  if (!hasHtmlTags) {
    return [raw];
  }

  // Browser-safe parsing
  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return Array.from(doc.querySelectorAll("li"))
      .map((li) => li.textContent?.trim() ?? "")
      .filter(Boolean);
  }

  // SSR / Node fallback
  const matches = html.match(/<li\b[^>]*>([\s\S]*?)<\/li>/gi) || [];

  return matches
    .map((item) =>
      item
        .replace(/<\/?li[^>]*>/gi, "") // remove <li>
        .replace(/<[^>]+>/g, "") // remove other tags
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean);
}

function buildOffersFromRegions(
  regions,
  pageUrl: string, // canonical page url
  taxRules: Record<string, { rate: number; text?: string }>
) {
  return regions
    .map((r) => {
      const regionName = r?.courseRegion?.value?.trim();
      const currency = r?.currencyCode?.value?.trim();
      const regionCode = (r?.name || "").trim().toLowerCase();

      // Advertise the non-member (public) price, but only when it is a real
      // positive amount. Some templates leave it blank (bundles) or set it to
      // "0" while members are still charged (e.g. multi-session) - neither is a
      // valid public price, so fall back to the member price instead of
      // advertising an empty or misleadingly-free offer.
      const rawNonMemberPrice = r?.nonMemberPrice?.value;
      const nonMemberPriceNumber = Number(rawNonMemberPrice);
      const hasValidNonMemberPrice =
        rawNonMemberPrice != null &&
        String(rawNonMemberPrice).trim() !== "" &&
        Number.isFinite(nonMemberPriceNumber) &&
        nonMemberPriceNumber > 0;
      const rawPrice = hasValidNonMemberPrice ? rawNonMemberPrice : r?.memberPrice?.value;

      const taxRule = taxRules[regionCode] || { rate: 0 };

      const priceStr =
        rawPrice === "" || rawPrice == null
          ? null
          : calcTaxInclusivePriceSafe(rawPrice, taxRule.rate);

      const price =
        priceStr === null || !Number.isFinite(Number(priceStr))
          ? null
          : Number(priceStr);

      if (!regionName || !currency || price === null) return null;

      const isGlobal = regionName.toLowerCase() === "global";

      return {
        "@type": "Offer",
        price: price, // schema accepts string/number; string is OK
        priceCurrency: currency.toUpperCase(),
        availability: "https://schema.org/InStock",
        url: pageUrl,
        ...(isGlobal
          ? { areaServed: { "@type": "Place", name: "Global" } }
          : { areaServed: { "@type": "Country", name: regionName } }),
      };
    })
    .filter(Boolean);
}

type AnyObj = Record<string, any>;

export type JssRendering = {
  placeholders?: Record<string, unknown>;
};

export type JssComponent = {
  uid?: string;
  componentName?: string;
  dataSource?: string;
  params?: AnyObj;
  fields?: AnyObj; // Sitecore JSS fields are flexible
};

export type CourseRegion = {
  id?: string;
  name?: string;
  courseRegion?: { value?: string };
  memberPrice?: { value?: string };
  nonMemberPrice?: { value?: string };
  currencyCode?: { value?: string };
  offeringURL?: { value?: string };
};

/**
 * Safely read nested value by path.
 * Example: getByPath(obj, ['fields','data','datasource','children'])
 */
function getByPath<T = unknown>(
  obj: unknown,
  path: Array<string | number>
): T | undefined {
  let cur: any = obj;
  for (const key of path) {
    if (cur == null) return undefined;
    cur = cur[key as any];
  }
  return cur as T;
}

/**
 * Get components array from rendering.placeholders[placeholderKey]
 * Always returns an array (empty if missing/invalid).
 */
function getPlaceholderComponents(
  rendering: JssRendering | null | undefined,
  placeholderKey: string
): JssComponent[] {
  const raw = rendering?.placeholders?.[placeholderKey];

  // In JSS, placeholder value should be an array of components
  if (Array.isArray(raw)) return raw as JssComponent[];

  // Defensive: sometimes data shape can be unexpected
  return [];
}

/**
 * Find the first component by componentName in a placeholder.
 */
function findComponent(
  rendering: JssRendering | null | undefined,
  placeholderKey: string,
  componentName: string
): JssComponent | null {
  const components = getPlaceholderComponents(rendering, placeholderKey);
  return components.find((c) => c?.componentName === componentName) ?? null;
}

type TaxSettingItem = {
  countryCode?: { value?: string };
  taxLabel?: { value?: string };
  taxPercentage?: { value?: string };
};

function getTaxSettingsFromStickyPanel(
  rendering: JssRendering | null | undefined,
  options?: {
    placeholderKey?: string;
    componentName?: string;
  }
): TaxSettingItem[] {
  const placeholderKey = options?.placeholderKey ?? "jss-content-sidebar";
  const componentName = options?.componentName ?? "CourseStickyPanel";

  const comp = findComponent(rendering, placeholderKey, componentName);
  if (!comp) return [];

  const taxSettings = getByPath<unknown>(comp, [
    "fields",
    "data",
    "taxConfiguration",
    "taxSettings",
  ]);

  return Array.isArray(taxSettings) ? (taxSettings as TaxSettingItem[]) : [];
}

/**
 * ✅ Main function you want:
 * Read Course Regions safely from:
 * rendering.placeholders['jss-content-sidebar'][*]
 *   -> componentName === 'CourseStickyPanel'
 *   -> fields.data.datasource.children
 */
export function getCourseRegionsFromRendering(
  rendering: JssRendering | null | undefined,
  options?: {
    placeholderKey?: string; // default: 'jss-content-sidebar'
    componentName?: string; // default: 'CourseStickyPanel'
  }
): CourseRegion[] {
  const placeholderKey = options?.placeholderKey ?? "jss-content-sidebar";
  const componentName = options?.componentName ?? "CourseStickyPanel";

  const comp = findComponent(rendering, placeholderKey, componentName);

  if (!comp) return [];

  const children = getByPath<unknown>(comp, [
    "fields",
    "data",
    "datasource",
    "children",
  ]);

  if (!Array.isArray(children)) return [];

  // Optional: filter out weird items, keep only objects
  return (children as unknown[]).filter(
    (x): x is CourseRegion => typeof x === "object" && x !== null
  );
}

type RelatedModule = {
  url?: string;
  displayName?: string;
  fields?: {
    marketingTitle?: { value?: string };
    description?: { value?: string };
    overview?: { value?: string };
  };
};

function toAbsoluteUrl(base: string, maybeRelative?: string) {
  if (!maybeRelative) return undefined;
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  return `${base}${maybeRelative.startsWith("/") ? "" : "/"}${maybeRelative}`;
}

function buildHasPartFromRelatedModules(
  rendering: any,
  baseUrl = "https://www.cpaaustralia.com.au"
) {
  const related = rendering?.fields?.relatedModules as
    | RelatedModule[]
    | undefined;

  if (!Array.isArray(related) || related.length === 0) return undefined;

  const hasPart = related
    .map((m) => {
      const name =
        m?.fields?.marketingTitle?.value?.trim() || m?.displayName?.trim();

      const url = toAbsoluteUrl(baseUrl, m?.url);

      if (!name || !url) return null;

      const desc = m?.fields?.description?.value?.trim();

      return {
        "@type": "Course",
        name,
        ...(desc ? { description: desc } : {}),
        url,
      };
    })
    .filter(Boolean);

  return hasPart.length ? hasPart : undefined;
}

type CourseSessionItem = {
  city?: { value?: string };
  region?: { value?: string };
  startDate?: { value?: string }; //  20260504T230000Z
  endDate?: { value?: string };
  sessionTitle?: { value?: string };
  description?: { value?: string }; // HTML
  sessionLink?: { value?: string };
  maxEnrollments?: { value?: string | number };
  enrollCount?: { value?: string | number };
  id?: string;
  code?: { value?: string };
};

function sitecoreToIsoUtc(sitecoreValue?: string): string | undefined {
  if (!sitecoreValue) return undefined;

  const m = moment.utc(sitecoreValue, "YYYYMMDD[T]HHmmss[Z]", true);
  if (!m.isValid()) return undefined;

  return m.toISOString();
}

function sessionToRequiredCourseInstance(session: any, courseMode: string) {
  const startDate = sitecoreToIsoUtc(session?.startDate?.value);

  let locationName = 'Online';

  if (courseMode === 'In person') {
    const city = session?.city?.value || '';
    const region = session?.region?.value || '';
    locationName = [city, region].filter(Boolean).join(', ') || 'Default - In person';
  }

  if (!startDate) return null;

  return {
    "@type": "CourseInstance",
    courseMode,
    startDate,
    location: {
      "@type": "Place",
      name: locationName,
    },
  };
}

function deriveCourseMode(rendering: any): string {
  const format = rendering?.fields?.format?.value?.trim() || "";

  const lower = format.toLowerCase();

  if (lower.includes("self-paced")) return "Online - Self-paced";
  if (lower.includes("online")) return "Online (live)";

  return "In person";
}

function getTabPlaceholderKeys(tabComponent: any): string[] {
  const ph = tabComponent?.placeholders;
  if (!ph || typeof ph !== "object") return [];
  return Object.keys(ph).filter((k) => /^tabcomponent-\d+$/i.test(k));
}

// Mirror the WP-5680 UI fix (hide sessions with a past date): keep a session
// only when its end date (or start date) is in the future/now, or unparseable.
function isFutureOrCurrentSession(session: CourseSessionItem): boolean {
  const dateToCheck = session?.endDate?.value || session?.startDate?.value;
  const timestamp = Date.parse(normalize(dateToCheck || ""));

  return Number.isNaN(timestamp) || timestamp >= Date.now();
}

function buildHasCourseInstanceFromRendering(
  rendering: any,
  options?: {
    mainPlaceholderKey?: string; // 'jss-content-main'
    tabComponentName?: string; // 'TabComponent'
    courseSessionName?: string; // 'CourseSession'
    offset?: string; // '+11:00'
  }
) {
  const mainPlaceholderKey = options?.mainPlaceholderKey ?? "jss-content-main";
  const tabComponentName = options?.tabComponentName ?? "TabComponent";
  const courseSessionName = options?.courseSessionName ?? "CourseSession";

  const main = rendering?.placeholders?.[mainPlaceholderKey];
  if (!Array.isArray(main)) return undefined;

  const tabComponents = main.filter(
    (c: any) => c?.componentName === tabComponentName
  );
  if (tabComponents.length === 0) return undefined;

  const allSessions: CourseSessionItem[] = [];

  for (const tabComp of tabComponents) {
    const tabKeys = getTabPlaceholderKeys(tabComp);

    for (const key of tabKeys) {
      const compsInTab = tabComp?.placeholders?.[key];
      if (!Array.isArray(compsInTab)) continue;

      const courseSessionComp = compsInTab.find(
        (x: any) => x?.componentName === courseSessionName
      );
      if (!courseSessionComp) continue;

      // fields.data.sessionGroup.sessions
      const sessions = courseSessionComp?.fields?.data?.sessionGroup?.sessions;
      if (Array.isArray(sessions)) {
        allSessions.push(...(sessions as CourseSessionItem[]));
      }
    }
  }

  if (allSessions.length === 0) return undefined;

  // Drop sessions whose date has already passed (matches the WP-5680 UI fix).
  const upcomingSessions = allSessions.filter(isFutureOrCurrentSession);
  if (upcomingSessions.length === 0) return undefined;

  const courseMode = deriveCourseMode(rendering);

  const instances = upcomingSessions
    .map((s) => sessionToRequiredCourseInstance(s, courseMode))
    .filter(Boolean);

  // Multi-session courses repeat each session once per region in the source data,
  // producing duplicate CourseInstances. Collapse by start date + location + mode.
  const seenInstances = new Set<string>();
  const uniqueInstances = instances.filter((instance: any) => {
    const key = `${instance.courseMode}|${instance.startDate}|${instance.location?.name ?? ""}`;
    if (seenInstances.has(key)) {
      return false;
    }
    seenInstances.add(key);
    return true;
  });

  return uniqueInstances.length ? uniqueInstances : undefined;
}


const stripTags = (s?: string) => (s ?? '').replace(/<[^>]*>/g, '').trim();


export function buildCourseStructuredData(
  context: any,
  rendering: any
): WithContext<any> | null {
  if (!context?.site?.name) {
    return null;
  }

  const url = context?.canonicalUrl;
  const inLang = context?.language;
  const sitebaseurl = `https${context?.siteInfo?.siteBaseUrl}`;
  const learningObjectivesHtml = rendering?.fields?.learningObjectives?.value;
  const taxSettings = getTaxSettingsFromStickyPanel(rendering);
  const taxRules = buildTaxRulesFromSettings(taxSettings, TAX_RULES);
  const regions = getCourseRegionsFromRendering(rendering);
  const hasPart = buildHasPartFromRelatedModules(rendering, sitebaseurl);
  const hasCourseInstance = buildHasCourseInstanceFromRendering(rendering);

  // page info
  const pageName = rendering?.fields?.pageTitle?.value || rendering?.fields?.offeringName?.value || "";
  const rawPageDes = rendering?.fields?.description?.value || rendering?.fields?.pageDescription?.value || '';
  const pageDes = stripTags(rawPageDes);

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": pageName, // SEO page name
    "description": pageDes, // SEO page description
    "url": url, // page URL

    "inLanguage": inLang,
    provider: {
      "@id": "https://www.cpaaustralia.com.au/#organization",
    },
    teaches: htmlListToStringArray(learningObjectivesHtml),
    offers: buildOffersFromRegions(regions, url, taxRules),
    ...(hasPart ? { hasPart } : {}),
    ...(hasCourseInstance ? { hasCourseInstance } : {}),
  };
}

/** Escape text so it is safe to embed inside server-rendered HTML markup. */
function escapeHtml(s?: string): string {
  return (s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Produce the human-readable price label for one region, mirroring
 * getPriceStatus() in CourseStickyPanel so the fallback text never drifts from
 * the visible UI: "Unavailable" | "Complimentary" | "{currency} {price} inc. {tax}".
 */
function fallbackPriceLabel(
  price: unknown,
  currency: string,
  taxRule: { rate: number; text?: string }
): string {
  const rawPrice = price == null ? "" : String(price).trim();
  if (rawPrice === "") return "Unavailable";

  const numericPrice = Number(rawPrice);
  if (!Number.isFinite(numericPrice) || numericPrice < 0) return "Unavailable";
  if (numericPrice === 0) return "Complimentary";

  const displayPrice = calcTaxInclusivePriceSafe(rawPrice, taxRule.rate);
  if (!displayPrice) return "Unavailable";

  const showTaxText = (taxRule?.rate ?? 0) > 0 && !!taxRule?.text;
  const label = [currency, displayPrice].filter(Boolean).join(" ");
  return showTaxText ? `${label} inc. ${taxRule.text}` : label;
}

/**
 * Build a plain-HTML fallback block (course name, description and every
 * region's member/non-member pricing) for embedding in a <noscript> tag during
 * SSR. Runtime/AI bots that don't execute JS - and therefore never see the
 * client-rendered sticky panel or the JSON-LD <script> - can read this. Returns
 * "" for non-course pages (no CourseStickyPanel regions), so it is a no-op
 * everywhere else. See WP-5733.
 */
export function buildCourseFallbackHtml(rendering: any): string {
  const regions = getCourseRegionsFromRendering(rendering);
  if (!regions.length) return "";

  const taxRules = buildTaxRulesFromSettings(
    getTaxSettingsFromStickyPanel(rendering),
    TAX_RULES
  );

  const name = rendering?.fields?.pageTitle?.value || rendering?.fields?.offeringName?.value || "";
  const description = stripTags(
    rendering?.fields?.description?.value || rendering?.fields?.pageDescription?.value || ""
  );

  const items = regions
    .map((r) => {
      const regionName = r?.courseRegion?.value?.trim();
      const currency = r?.currencyCode?.value?.trim() || "";
      const regionCode = (r?.name || "").trim().toLowerCase();
      if (!regionName) return "";

      const taxRule = taxRules[regionCode] || { rate: 0 };
      const member = fallbackPriceLabel(r?.memberPrice?.value, currency, taxRule);
      const nonMember = fallbackPriceLabel(r?.nonMemberPrice?.value, currency, taxRule);

      // List every available price (members and non-members) under the region
      // so the fallback mirrors the on-page pricing as closely as possible.
      // Skip "Unavailable" prices and drop the region when neither is available.
      // See WP-5741.
      const priceItems: string[] = [];
      if (member !== "Unavailable") priceItems.push(`<li>Members: ${escapeHtml(member)}</li>`);
      if (nonMember !== "Unavailable") priceItems.push(`<li>Non-members: ${escapeHtml(nonMember)}</li>`);
      if (!priceItems.length) return "";

      return `<li>${escapeHtml(regionName)}<ul>${priceItems.join("")}</ul></li>`;
    })
    .filter(Boolean)
    .join("");

  if (!items) return "";

  return (
    `<noscript>` +
    `<section aria-label="Course pricing">` +
    (name ? `<h1>${escapeHtml(name)}</h1>` : "") +
    (description ? `<p>${escapeHtml(description)}</p>` : "") +
    `<p>Course pricing by region:</p>` +
    `<ul>${items}</ul>` +
    `</section>` +
    `</noscript>`
  );
}

export const CourseStructureData: React.FC<any> = ({ context, rendering }) => {
  const courseStructureData = buildCourseStructuredData(context, rendering);

  if (!courseStructureData) {
    return null;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(courseStructureData)}
      </script>
    </Helmet>
  );
};